// Timestamped segment list, used live (recorder) and static (detail).

import { html, LitElement } from "lit";
import { fmtMs, type MeetingEvent, type Segment } from "../lib/types";

const EVENT_LABEL: Record<MeetingEvent["kind"], string> = {
  joined: "joined the meeting",
  left: "left the meeting",
  removed: "was removed by the host",
};

export class MmTranscript extends LitElement {
  static override properties = {
    segments: { attribute: false },
    markers: { attribute: false },
    highlightMs: { attribute: false },
  };

  segments: Segment[] = [];
  /** Presence events interleaved by at_ms (plan 0004). */
  markers: MeetingEvent[] = [];
  highlightMs: number | null = null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override updated(): void {
    if (this.highlightMs !== null) {
      const el = this.querySelector(`[data-start="${this.highlightMs}"]`);
      el?.scrollIntoView({ block: "center" });
    }
  }

  override render() {
    if (this.segments.length === 0 && this.markers.length === 0) {
      return html`<p class="empty">No transcript yet.</p>`;
    }
    // Merge segments + presence markers into one timeline.
    type Row =
      | { ms: number; ord: number; seg: Segment }
      | { ms: number; ord: number; ev: MeetingEvent };
    const rows: Row[] = [
      ...this.segments.map((seg) => ({ ms: seg.start_ms, ord: seg.id, seg })),
      ...this.markers.map((ev) => ({ ms: ev.at_ms, ord: ev.id, ev })),
    ].sort((a, b) => a.ms - b.ms || a.ord - b.ord);
    return html`<div class="transcript">
      ${rows.map((row) =>
        "seg" in row
          ? html`
              <div
                class="transcript-line ${row.seg.start_ms === this.highlightMs
                  ? "flash"
                  : ""}"
                data-start=${row.seg.start_ms}
              >
                <span class="transcript-time">${fmtMs(row.seg.start_ms)}</span>
                <span>
                  ${row.seg.speaker
                    ? html`<strong class="muted">${row.seg.speaker}:</strong> `
                    : ""}${row.seg.text}
                </span>
              </div>
            `
          : html`
              <div class="transcript-line transcript-marker">
                <span class="transcript-time">${fmtMs(row.ev.at_ms)}</span>
                <span class="muted">
                  — ${row.ev.display_name} ${EVENT_LABEL[row.ev.kind]} —
                </span>
              </div>
            `,
      )}
    </div>`;
  }
}

customElements.define("mm-transcript", MmTranscript);
